import { useCallback } from 'react'

import {
	useLazyGetStructureMetadataQuery,
	useLazyGetStructureTreeQuery,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { logError } from '@/utils/errorHandling'

import { toTreeItem } from '../../constants'
import { StructureTreeItem } from '../../types'

interface UseRevealItemOptions {
	childrenMap: Record<string, StructureTreeItem[]>,
	setRawChildrenMap: React.Dispatch<React.SetStateAction<Record<string, StructureTreeItem[]>>>,
	setUsedIds: React.Dispatch<React.SetStateAction<Set<string>>>
	systems: StructureTreeItem[]
}

type RevealItemFn = (
	itemId: string,
	expandedItems: string[],
	onExpand: (newExpanded: string[]) => void,
) => Promise<void>

export const useRevealItem = ({
	systems,
	childrenMap,
	setUsedIds,
	setRawChildrenMap,
}: UseRevealItemOptions): RevealItemFn => {
	const [fetchStructureTree] = useLazyGetStructureTreeQuery()
	const [fetchStructureMetadata] = useLazyGetStructureMetadataQuery()

	return useCallback(
		async (itemId, expandedItems, onExpand) => {
			const structureId = Number(itemId)
			if (isNaN(structureId)) return

			const existingElement = document.querySelector(`[data-id="${itemId}"]`)
			if (existingElement) {
				existingElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
				return
			}

			try {
				const ancestorPath = findAncestorPathInCache(itemId, systems, childrenMap)
				
				if (ancestorPath) {
					const newExpanded = [...new Set([...expandedItems, ...ancestorPath])]
					onExpand(newExpanded)
					scrollToItemWithDelay(itemId)
					return
				}

				const metadata = await fetchStructureMetadata({ structureId }).unwrap()
				if (!metadata.fullPath) return

				const pathParts = metadata.fullPath.split('/').filter(Boolean)
				if (pathParts.length === 0) return

				const system = systems.find(
					(s) => s.label === pathParts[0] || s.code === pathParts[0],
				)
				if (!system) return

				const result = await fetchStructureTree({
					structureId: Number(system.structureId),
					maxLevel: 100,
				}).unwrap()

				const { grouped, allIds } = buildGroupedDescendants(result, system.id)

				setUsedIds((prev) => new Set([...prev, ...allIds]))
				setRawChildrenMap((prev) => ({ ...prev, ...grouped }))

				const ancestorIds = buildAncestorPath(metadata, result, system.id)
				const newExpanded = [...new Set([...expandedItems, ...ancestorIds])]
				onExpand(newExpanded)

				scrollToItemWithDelay(itemId)
			} catch (error) {
				logError(error, {
					operation: 'useRevealItem.revealItem',
					details: { itemId },
				})
			}
		},
		[systems, childrenMap, fetchStructureMetadata, fetchStructureTree, setUsedIds, setRawChildrenMap],
	)
}

const findAncestorPathInCache = (
	itemId: string,
	systems: StructureTreeItem[],
	childrenMap: Record<string, StructureTreeItem[]>,
): string[] | null => {
	const allItems = new Map<string, StructureTreeItem>()
	
	systems.forEach((s) => allItems.set(s.id, s))
	Object.values(childrenMap).flat().forEach((item) => allItems.set(item.id, item))

	const targetItem = allItems.get(itemId)
	if (!targetItem) return null

	const ancestorIds: string[] = []
	let currentItem: StructureTreeItem | undefined = targetItem

	while (currentItem?.parentStructureId) {
		const parentId = String(currentItem.parentStructureId)
		const parent = allItems.get(parentId)
		
		if (!parent) return null
		
		ancestorIds.unshift(parentId)
		currentItem = parent
	}

	return ancestorIds
}

const buildGroupedDescendants = (
	descendants: { id?: number; parent?: { id?: number } }[],
	systemId: string,
): { allIds: string[], grouped: Record<string, StructureTreeItem[]>; } => {
	const grouped: Record<string, StructureTreeItem[]> = { [systemId]: [] }
	const allIds: string[] = []
	const nodeIdsWithChildren = new Set<string>()

	descendants.forEach((item) => {
		const parentId = String(item.parent?.id)
		nodeIdsWithChildren.add(parentId)
		if (!grouped[parentId]) {
			grouped[parentId] = []
		}
		const treeItem = toTreeItem(item)
		grouped[parentId].push(treeItem)
		allIds.push(treeItem.id)
	})

	Object.values(grouped).forEach((children) => {
		children.forEach((child) => {
			child.hasChildren = nodeIdsWithChildren.has(child.id)
		})
	})

	return { grouped, allIds }
}

const buildAncestorPath = (
	metadata: { parent?: { id?: number } },
	descendants: { id?: number; parent?: { id?: number } }[],
	systemId: string,
): string[] => {
	const ancestorIds: string[] = []
	let currentId: number | undefined = metadata.parent?.id

	while (currentId) {
		ancestorIds.unshift(String(currentId))
		const parent = descendants.find((d) => d.id === currentId)
		currentId = parent?.parent?.id
	}

	if (!ancestorIds.includes(systemId)) {
		ancestorIds.unshift(systemId)
	}

	return ancestorIds
}

const scrollToItemWithDelay = (itemId: string): void => {
	setTimeout(() => {
		const element = document.querySelector(`[data-id="${itemId}"]`)
		element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
	}, 100)
}
